import api from '@/helpers/api'
import isExtranetHostname from '@/helpers/isExtranet';
import storageService from "@/services/storageService";
import { Capacitor } from '@capacitor/core'
import { pushNotificationHandler } from '@/mobile/plugins/pushNotification'
import googleAuthService from '@/services/googleAuth';
import appleAuthService from '@/services/appleAuth';

const withTimeout = (promise, ms = 3000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('Native call timeout')), ms))
  ]);
};

/**
 * Hapus semua key auth dari localStorage.
 * Dipakai oleh logout, logoutError, dan expireSession agar daftarnya konsisten.
 */
const clearLocalStorageAuth = () => {
  const AUTH_KEYS = [
    'token', 'username', 'branch_id', 'corporate_id', 'email',
    'id', 'member', 'merchant_id', 'level', 'profile_name',
    'selected_business_profile', 'type', 'tierId',
    'extranet_selected_tenant', 'user',
  ];
  AUTH_KEYS.forEach(key => localStorage.removeItem(key));
};

const cascadeNativeLogout = async (provider) => {
  if (!Capacitor.isNativePlatform() || !provider) return;

  if (provider === 'google') {
    try {
      await withTimeout(googleAuthService.logout(), 3000);
    } catch (e) {
      console.warn('[Auth] Google native signOut error (non-fatal):', e);
    }
  } else if (provider === 'apple') {
    try {
      await withTimeout(appleAuthService.logout(), 3000);
    } catch (e) {
      console.warn('[Auth] Apple native signOut error (non-fatal):', e);
    }
  }
};

let hydrationPromise = null;

export default {
  namespaced: true,
  state: {
    status: '',
    token: localStorage.getItem('token') || '',
    currUser: (() => {
      try {
        const stored = localStorage.getItem('user');
        return stored ? JSON.parse(stored) : {};
      } catch (e) {
        return {};
      }
    })(),
    businessProfiles: [],
    selectedBusinessProfile: JSON.parse(localStorage.getItem('selected_business_profile')) || {
      corporate_id: "",
      branch_id: "",
      merchant_id: "",
    },
    user_name: localStorage.getItem('username') || '',
    userInfo: {},
    selfLoaded: false,
    loginProvider: localStorage.getItem('login_provider') || null,
    isLoggingOut: false,
    isHydrated: false
  },
  mutations: {
    set_hydrated(state, val) {
      state.isHydrated = val;
    },
    auth_request(state) {
      state.status = 'loading';
    },
    auth_success(state, { token, user }) {
      state.status = 'success';
      state.token = token;
      state.currUser = user;
    },
    auth_complete(state) {
      state.status = 'success';
    },
    set_login_provider(state, provider) {
      state.loginProvider = provider;
      if (provider) {
        localStorage.setItem('login_provider', provider);
      } else {
        localStorage.removeItem('login_provider');
      }
    },
    set_logging_out(state, val) {
      state.isLoggingOut = val;
    },
    auth_error(state) {
      state.status = 'error';
    },
    auth_2fa_pending(state) {
      state.status = '2fa_pending';
    },
    logout(state) {
      state.status = '';
      state.token = '';
      state.currUser = {};
    },
    expire_session(state) {
      state.status = '';
      state.token = '';
      state.currUser = {};
      state.userInfo = {};
      state.selfLoaded = false;
      state.businessProfiles = [];
      state.selectedBusinessProfile = { corporate_id: "", branch_id: "", merchant_id: "" };
    },
    read_success(state) {
      state.status = 'success';
    },
    read_business_profile(state, data) {
      state.businessProfiles = { ...data };
    },
    select_business_profile(state, { data }) {
      state.selectedBusinessProfile = { ...data };
    },
    session_generated(state, session) {
      state.session = session;
    },
    user_info(state, userInfo) {
      state.userInfo = userInfo;
    },
    self_loaded(state) {
      state.selfLoaded = true;
    }
  },
  actions: {
    async restoreSession({ commit, state }) {
      if (state.isHydrated) return !!state.token;
      if (hydrationPromise) return hydrationPromise;

      hydrationPromise = (async () => {
        try {
          if (Capacitor.isNativePlatform()) {
            await storageService.restoreFromNative();
          }

          const token = localStorage.getItem('token');
          if (token) {
            let user = {};
            try {
              const storedUser = localStorage.getItem('user');
              user = storedUser ? JSON.parse(storedUser) : {};
            } catch (e) {
              user = {};
            }

            commit('auth_success', { token, user });

            const storedProfile = localStorage.getItem('selected_business_profile');
            if (storedProfile) {
              try {
                const parsedProfile = JSON.parse(storedProfile);
                commit('select_business_profile', { data: parsedProfile });
              } catch (e) {
                // ignore
              }
            }

            // Sync/verify push notification device registration on native platforms
            if (Capacitor.isNativePlatform()) {
              pushNotificationHandler.registerDevice().catch((error) => {
                console.warn('[Auth] Push notification registration error during restoreSession:', error);
              });
            }
          }
        } catch (error) {
          console.warn('[Auth] restoreSession error:', error);
        } finally {
          commit('set_hydrated', true);
          hydrationPromise = null;
        }
        return !!state.token;
      })();

      return hydrationPromise;
    },
    register({ commit }, user) {
      commit('auth_request');
      return new Promise((resolve, reject) => {
        api.request('post', '/api/auth/register', {}, user)
          .then(resp => {
            const token = resp.data.data.token;
            const user = resp.data.data;

            localStorage.setItem('token', token);
            localStorage.setItem('username', user.name);

            // api.defaults.headers.common['Authorization'] = token;
            commit('set_login_provider', 'email');
            commit('auth_success', { token, user });
            resolve(resp);
          }).catch(err => {
            commit('auth_error');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            reject(err);
          });
      });
    },
    checkLogin({ commit, dispatch }, user) {
      commit('auth_request');
      return new Promise((resolve, reject) => {
        user.autoLogin = user.autoLogin || false;
        if (user.merchant_id && user.merchant_id !== "" && user.autoLogin === false) {
          user.type = "web_temp_access";
        } else {
          user.merchant_id = "";
          delete user.autoLogin;
        }
        api.request('post', '/api/auth/check-login', {}, user)
          .then(resp => {
            if (resp.data.data?.type === "web_temp_access") {
              commit('auth_complete');
              const token = resp.data.data.token;
              const url = resp.data.data.ecommerce_url;
              const type = resp.data.data.type;
              const username = resp.data.data.name;
              const tierId = resp.data.data.tier_id;
              const first_name = resp.data.data.first_name;
              const last_name = resp.data.data.last_name;
              const email = resp.data.data.email;
              const phone = resp.data.data.phone;
              const title = resp.data.data.title;
              const corporate_id = resp.data.data.corporate_id;
              const tier_name = resp.data.data.tier_name;
              const point = resp.data.data.point;
              const voucher = resp.data.data.voucher;
              const media = resp.data.data.media;
              const res = {
                token,
                url,
                type,
                username,
                tierId,
                first_name,
                last_name,
                email,
                phone,
                title,
                corporate_id,
                tier_name,
                point,
                voucher,
                media,
              };
              if (localStorage.getItem('level') === null && localStorage.getItem('level') === 'member') {
                localStorage.setItem('token', res.token);
                localStorage.setItem('username', res.username);
                localStorage.setItem('tierId', res.tierId);
              }

              if (res.url !== null) {
                const urlParent = `${res.url}?member_token=${res.token}`;
                const message = {
                  type: 'login',
                  data: 'loginSucceed',
                  username: res.username,
                  token: res.token,
                  tierId: res.tierId,
                  tier_name: res.tier_name,
                  point: res.point,
                  voucher: res.voucher,
                  media: res.media,
                  url: urlParent
                };

                window.parent.postMessage(message, '*');
              } else {
                const message = {
                  type: 'login',
                  data: 'loginFailed'
                };
                window.parent.postMessage(message, '*');
              }

              return resolve(res);

            } else {
              const token = resp.data.data.token;
              const user = resp.data.data;

              // Reset 2FA state since login succeeded without 2FA
              commit('TwoFactor/RESET_2FA_STATE', null, { root: true });

              localStorage.setItem('token', token);
              localStorage.setItem('username', user.name);
              localStorage.setItem('branch_id', user.branch_id);
              localStorage.setItem('corporate_id', user.corporate_id);
              localStorage.setItem('email', user.email);
              localStorage.setItem('id', user.id);
              localStorage.setItem('member', user.member);
              localStorage.setItem('merchant_id', user.merchant_id);
              localStorage.setItem('level', user.level);
              localStorage.setItem("user", JSON.stringify(user));

              // api.defaults.headers.common['Authorization'] = token;

              commit("set_login_provider", "email");
              commit("auth_success", { token, user });
              const data = {
                corporate_id: user.corporate_id,
                branch_id: user.branch_id,
                merchant_id: user.merchant_id,
              };

              localStorage.setItem(
                "selected_business_profile",
                JSON.stringify(data),
              );
              commit("select_business_profile", { data });

              // Sync ke native storage untuk auto-login di mobile
              storageService.syncToNative().catch((err) => {
                console.error(
                  "[Auth] Failed to sync to native storage after checkLogin:",
                  err,
                );
              });

              // Load business profile AND user permissions
              dispatch('read')
                .then(() => dispatch('auth_check'))
                .then(async () => {
                  console.log('[Auth] User permissions loaded successfully after checkLogin');
                  
                  // Register for push notifications (mobile only)
                  if (Capacitor.isNativePlatform()) {
                    try {
                      await pushNotificationHandler.registerDevice();
                    } catch (error) {
                      console.error('[Auth] Push notification registration failed after checkLogin:', error);
                    }
                  }
                  
                  resolve(user);
                })
                .catch(err => {
                  console.error('[Auth] Error loading user data after checkLogin:', err);
                  resolve(user);
                });
            }

          }).catch(err => {
            commit('auth_error');
            reject(err);
          });
      });
    },
    login({ commit, dispatch }, user) {
      commit('auth_request');
      return new Promise((resolve, reject) => {
        user.autoLogin = user.autoLogin || false;
        const isNative = user.platform === 'native';
        if (!isNative && user.merchant_id && user.merchant_id !== "" && user.autoLogin === false) {
          user.type = "web_temp_access";
        } else {
          user.merchant_id = "";
          delete user.autoLogin;
        }

        const isExtranet = isExtranetHostname();
        const url = isExtranet ? '/api/extranet/auth/login' : '/api/auth/login';

        api.request('post', url, {}, user)
          .then(resp => {
            if (resp.data.data && resp.data.data.requires_2fa) {
              commit('TwoFactor/SET_TEMP_TOKEN', resp.data.data.temp_token, { root: true });
              commit('TwoFactor/SET_SHOW_VERIFY_MODAL', true, { root: true });
              commit('auth_2fa_pending');
              return resolve({ requires_2fa: true });
            }

            if (resp.data.data?.type === "web_temp_access") {
              commit('auth_complete');
              const token = resp.data.data.token;
              const url = resp.data.data.ecommerce_url;
              const type = resp.data.data.type;
              const username = resp.data.data.name;
              const tierId = resp.data.data.tier_id;
              const first_name = resp.data.data.first_name;
              const last_name = resp.data.data.last_name;
              const email = resp.data.data.email;
              const phone = resp.data.data.phone;
              const title = resp.data.data.title;
              const corporate_id = resp.data.data.corporate_id;
              const tier_name = resp.data.data.tier_name;
              const tier_icon = resp.data.data.tier_icon;
              const point = resp.data.data.point;
              const voucher = resp.data.data.voucher;
              const media = resp.data.data.media;
              const level = resp.data.data.level;
              const member = resp.data.data.member;
              const id = resp.data.data.id;
              const res = {
                token,
                url,
                type,
                username,
                tierId,
                first_name,
                last_name,
                email,
                phone,
                title,
                corporate_id,
                tier_name,
                tier_icon,
                point,
                voucher,
                media,
                level,
                member,
                id
              };
              if (localStorage.getItem('level') === null && localStorage.getItem('level') === 'member') {
                localStorage.setItem('token', res.token);
                localStorage.setItem('username', res.username);
                localStorage.setItem('tierId', res.tierId);
              }

              if (res.url !== null) {
                const urlParent = `${res.url}?member_token=${res.token}`;
                const message = {
                  type: 'login',
                  data: 'loginSucceed',
                  username: res.username,
                  token: res.token,
                  tierId: res.tierId,
                  tier_name: res.tier_name,
                  tier_icon: res.tier_icon,
                  point: res.point,
                  voucher: res.voucher,
                  media: res.media,
                  url: urlParent
                };

                window.parent.postMessage(message, '*');
              } else {
                const message = {
                  type: 'login',
                  data: 'loginFailed'
                };
                window.parent.postMessage(message, '*');
              }

              return resolve(res);

            } else {
              const token = resp.data.data.token;
              const user = resp.data.data;

              // Reset 2FA state since login succeeded without 2FA
              commit('TwoFactor/RESET_2FA_STATE', null, { root: true });

              localStorage.setItem('token', token);
              localStorage.setItem('username', user.name);
              localStorage.setItem('email', user.email);
              localStorage.setItem('id', user.id);
              localStorage.setItem('level', user.level);
              localStorage.setItem("user", JSON.stringify(user));

              // Extranet: skip business_profile (tenant-only)
              if (isExtranet) {
                commit('set_login_provider', 'email');
                commit('auth_success', { token, user });
                commit('self_loaded');

                dispatch('auth_check')
                  .then(() => {
                    console.log('[Auth] Extranet user loaded successfully after login');
                    resolve(user);
                  })
                  .catch(err => {
                    console.error('[Auth] Error loading extranet user after login:', err);
                    resolve(user);
                  });
              } else {
                // Tenant flow: set business profile + load full data
                localStorage.setItem('branch_id', user.branch_id);
                localStorage.setItem('corporate_id', user.corporate_id);
                localStorage.setItem('member', user.member);
                localStorage.setItem('merchant_id', user.merchant_id);

                commit('set_login_provider', 'email');
                commit('auth_success', { token, user });
                const data = {
                  corporate_id: user.corporate_id,
                  branch_id: user.branch_id,
                  merchant_id: user.merchant_id
                };

                localStorage.setItem('selected_business_profile', JSON.stringify(data));
                commit('select_business_profile', { data });

                // Sync ke native storage untuk auto-login di mobile
                storageService.syncToNative().catch((err) => {
                  console.error(
                    "[Auth] Failed to sync to native storage after login:",
                    err,
                  );
                });

                // Load business profile AND user permissions
                dispatch('read')
                  .then(() => dispatch('auth_check'))
                  .then(async () => {
                    console.log('[Auth] User permissions loaded successfully after login');

                    // Register for push notifications (mobile only)
                    if (Capacitor.isNativePlatform()) {
                      try {
                        await pushNotificationHandler.registerDevice();
                      } catch (error) {
                        console.error('[Auth] Push notification registration failed after login:', error);
                      }
                    }

                    resolve(user);
                  })
                  .catch(err => {
                    console.error('[Auth] Error loading user data after login:', err);
                    resolve(user);
                  });
              }
            }
          }).catch(err => {
            commit('auth_error');
            reject(err);
          });
      });
    },
    /**
     * Backoffice Login - dedicated endpoint for admin users
     * Part of the new authentication module
     */
    backofficeLogin({ commit, dispatch }, user) {
      commit('auth_request');
      return new Promise((resolve, reject) => {
        api.request('post', '/api/backoffice/login', {}, user)
          .then(resp => {
            if (resp.data.data && resp.data.data.requires_2fa) {
              commit('TwoFactor/SET_TEMP_TOKEN', resp.data.data.temp_token, { root: true });
              commit('TwoFactor/SET_SHOW_VERIFY_MODAL', true, { root: true });
              commit('auth_2fa_pending');
              return resolve({ requires_2fa: true });
            }

            const token = resp.data.data.token;
            const userData = resp.data.data;

            commit('TwoFactor/RESET_2FA_STATE', null, { root: true });

            localStorage.setItem('token', token);
            localStorage.setItem('username', userData.name);
            localStorage.setItem('email', userData.email);
            localStorage.setItem('id', userData.id);
            localStorage.setItem('level', userData.level);
            localStorage.setItem('branch_id', userData.branch_id);
            localStorage.setItem('corporate_id', userData.corporate_id);
            localStorage.setItem('member', userData.member);
            localStorage.setItem('merchant_id', userData.merchant_id);

            commit('set_login_provider', 'email');
            commit('auth_success', { token, user: userData });
            const data = {
              corporate_id: userData.corporate_id,
              branch_id: userData.branch_id,
              merchant_id: userData.merchant_id
            };

            localStorage.setItem('selected_business_profile', JSON.stringify(data));
            commit('select_business_profile', { data });

            dispatch('read')
              .then(() => dispatch('auth_check'))
              .then(() => {
                console.log('[Auth] Backoffice user loaded successfully after login');
                resolve(userData);
              })
              .catch(err => {
                console.error('[Auth] Error loading backoffice user data after login:', err);
                resolve(userData);
              });
          }).catch(err => {
            commit('auth_error');
            reject(err);
          });
      });
    },
    /**
     * Member Login - dedicated endpoint for member users
     * Part of the new authentication module
     */
    memberLogin({ commit, dispatch }, user) {
      commit('auth_request');
      return new Promise((resolve, reject) => {
        api.request('post', '/api/member/login', {}, user)
          .then(resp => {
            if (resp.data.data && resp.data.data.requires_2fa) {
              commit('TwoFactor/SET_TEMP_TOKEN', resp.data.data.temp_token, { root: true });
              commit('TwoFactor/SET_SHOW_VERIFY_MODAL', true, { root: true });
              commit('auth_2fa_pending');
              return resolve({ requires_2fa: true });
            }

            const token = resp.data.data.token;
            const userData = resp.data.data;

            commit('TwoFactor/RESET_2FA_STATE', null, { root: true });

            localStorage.setItem('token', token);
            localStorage.setItem('username', userData.name);
            localStorage.setItem('email', userData.email);
            localStorage.setItem('id', userData.id);
            localStorage.setItem('level', userData.level);
            localStorage.setItem('corporate_id', userData.corporate_id);
            localStorage.setItem('member', JSON.stringify(userData.member || true));
            localStorage.setItem('user', JSON.stringify(userData));

            commit('set_login_provider', 'email');
            commit('auth_success', { token, user: userData });

            if (userData.ecommerce_url) {
              const urlParent = `${userData.ecommerce_url}?member_token=${token}`;
              const message = {
                type: 'login',
                data: 'loginSucceed',
                username: userData.name,
                token: token,
                tierId: userData.tier_id,
                tier_name: userData.tier_name,
                tier_icon: userData.tier_icon,
                point: userData.point,
                voucher: userData.voucher,
                media: userData.media,
                url: urlParent
              };
              window.parent.postMessage(message, '*');
            }

            // Sync to native storage for auto-login (mobile only)
            if (Capacitor.isNativePlatform()) {
              storageService.syncToNative().catch((err) => {
                console.error('[Auth] Failed to sync to native storage after memberLogin:', err);
              });
            }

            dispatch('auth_check')
              .then(async () => {
                console.log('[Auth] Member user loaded successfully after login');

                // Register push notifications (mobile only)
                if (Capacitor.isNativePlatform()) {
                  try {
                    await pushNotificationHandler.registerDevice();
                  } catch (error) {
                    console.error('[Auth] Push notification registration failed after memberLogin:', error);
                  }
                }

                resolve(userData);
              })
              .catch(err => {
                console.error('[Auth] Error loading member user data after login:', err);
                resolve(userData);
              });
          }).catch(err => {
            commit('auth_error');
            reject(err);
          });
      });
    },
    async logout({ commit, state }) {
      if (state.isLoggingOut) return;

      commit('set_logging_out', true);

      try {
        await cascadeNativeLogout(state.loginProvider);
        commit('set_login_provider', null);

        // Simpan token sebelum melakukan proses logout
        const currentToken = localStorage.getItem('token');

        //  Unregister push notifications TERLEBIH DAHULU selagi token auth masih aktif
        if (Capacitor.isNativePlatform() && currentToken) {
          try {
            await pushNotificationHandler.unregisterDevice();
          } catch (error) {
            console.warn(
              '[Auth] Push notification unregistration warning during logout:',
              error
            );
          }
        }

        // Revoke token di server
        if (currentToken) {
          const isExtranet = isExtranetHostname();
          const url = isExtranet
            ? '/api/extranet/auth/logout'
            : '/api/auth/logout';

          try {
            await api.request('post', url, {}, null, {
              headers: {
                'Authorization': 'Bearer ' + currentToken,
                'type': 'auth_token'
              }
            });

            console.log('[Logout] Logout API success');
          } catch (error) {
            if (error?.response?.status === 401) {
              console.warn('[Auth] Logout API: session already invalidated on server (401).');
            } else {
              console.error('[Auth] Logout API error:', error);
            }
          }
        }

        // Baru bersihkan localStorage
        clearLocalStorageAuth();

        // Clear native storage
        try {
          await storageService.clearAuthData();
        } catch (err) {
          console.error(
            '[Auth] Failed to clear native storage on logout:',
            err
          );
        }

        // Clear Vuex state
        commit('logout');

      } finally {
        commit('set_logging_out', false);
      }
    },
    async expireLocal({ commit, state }) {
      if (state.isLoggingOut) return;
      commit('set_logging_out', true);

      try {
        // Sign-out Google/Apple native bersifat lokal di device, tanpa backend kita
        await cascadeNativeLogout(state.loginProvider);
        commit('set_login_provider', null);

        clearLocalStorageAuth();

        try {
          await storageService.clearAuthData();
        } catch (err) {
          console.error('[Auth] Failed to clear native storage on expire:', err);
        }

        commit('expire_session');
      } finally {
        commit('set_logging_out', false);
      }
    },
    async logoutError({ commit, state }, token) {
      if (state.isLoggingOut) return;
      commit('set_logging_out', true);

      try {
        await cascadeNativeLogout(state.loginProvider);
        commit('set_login_provider', null);

        commit('logout');

        // Unregister push notifications first (mobile only)
        if (Capacitor.isNativePlatform()) {
          try {
            await pushNotificationHandler.unregisterDevice();
          } catch (error) {
            console.error('[Auth] Push notification unregistration failed during logoutError:', error);
            // Don't block logout flow
          }
        }

        return new Promise((resolve) => {
          const isExtranet = isExtranetHostname();
          const url = isExtranet ? '/api/extranet/auth/logout' : '/api/auth/logout';

          api.request('post', url, {}, {
            headers: {
              'Authorization': 'Bearer ' + token,
              'type': 'auth_token'
            }
          }).then(() => {
            clearLocalStorageAuth();
            // Clear native storage
            storageService.clearAuthData().catch((err) => {
              console.error(
                "[Auth] Failed to clear native storage on logoutError:",
                err,
              );
            });
            return resolve();
          }).catch(err => {
            // Tetap bersihkan data lokal meski API logout gagal
            console.error('[Auth] logoutError API call failed, clearing local session anyway:', err);
            clearLocalStorageAuth();
            storageService.clearAuthData().catch(() => {});
            return resolve();
          });
        });
      } finally {
        commit('set_logging_out', false);
      }
    },
    read({ commit, state }) {
      return new Promise((resolve, reject) => {
        const user = state.currUser;

        api.request('get', `/api/business_profile/${user.id}`, {
          headers: {
            'Authorization': 'Bearer ' + localStorage.getItem('token'),
          }
        })
          .then((result) => {
            commit('read_success');
            commit('read_business_profile', result.data.data);

            const resSelectedBusinessProfile = JSON.parse(localStorage.getItem('selected_business_profile')) ?? {
              corporate_id: null,
              branch_id: null,
              merchant_id: null,
            };

            const data_corporate = result.data.data?.corporate ? [...result.data.data?.corporate] : [];
            const user_level = state.currUser.level;

            if (['corporate', 'branch', 'merchant'].includes(user_level)) {
              resSelectedBusinessProfile.corporate_id = data_corporate[0]?.id;
              resSelectedBusinessProfile.corporate_name = data_corporate[0]?.name;

              if (['branch', 'merchant'].includes(user_level)) {
                resSelectedBusinessProfile.branch_id = data_corporate[0].branch[0]?.id;
                resSelectedBusinessProfile.branch_name = data_corporate[0].branch[0]?.name;

                if (['merchant'].includes(user_level)) {
                  resSelectedBusinessProfile.merchant_id = data_corporate[0].branch[0].merchant[0]?.id;
                  resSelectedBusinessProfile.merchant_name = data_corporate[0].branch[0].merchant[0]?.name;
                }
              }

            }

            commit('select_business_profile', { data: resSelectedBusinessProfile });
            localStorage.setItem('selected_business_profile', JSON.stringify(resSelectedBusinessProfile));

            const selectedBusinessProfile = { ...state.selectedBusinessProfile };
            resolve(selectedBusinessProfile);
          })
          .catch((err) => {
            commit('auth_error');
            reject(err);
          });
      });
    },
    auth_check({ commit }) {
      const token = localStorage.getItem('token') ?
        localStorage.getItem('token').includes('"') ?
          localStorage.getItem('token').replace(/"/g, '') :
          localStorage.getItem('token') : '';

      if (token) {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          let value = localStorage.getItem(key);

          if (value && value.includes('"') && !(value.startsWith('{') && value.endsWith('}'))) {
            const newValue = value.replace(/"/g, '');
            localStorage.setItem(key, newValue);
          }
        }
      }
      return new Promise((resolve, reject) => {
        const isExtranet = isExtranetHostname();
        const url = isExtranet ? '/api/extranet/auth/self' : '/api/auth/self';

        api.request('get', url, {
          headers: {
            'Authorization': 'Bearer ' + token,
          }
        })
          .then(result => {
            const user = {
              id: result.data.data.id,
              email: result.data.data.email,
              level: result.data.data.levels.at(0).name,
              title: result.data.data.title,
              first_name: result.data.data.first_name,
              last_name: result.data.data.last_name,
              member: result.data.data.member,
              two_factor_confirmed_at: result.data.data.two_factor_confirmed_at
            };

            if (result.data.data.baseDomain) {
              localStorage.setItem('baseDomain', result.data.data.baseDomain);
            }

            if (result.data.data.id) {
              localStorage.setItem("id", result.data.data.id);
            }

            commit('auth_success', { token, user });
            commit('user_info', result.data.data);
            commit('self_loaded');
            resolve(result.data);
          })
          .catch(err => {
            const status = err?.response?.status;
            // Only clear user session data when token is definitively expired/invalid (401 Unauthorized)
            // For network errors (offline), 403, 500, etc., preserve stored session
            if (status === 401) {
              localStorage.removeItem('username');
              localStorage.removeItem('user');
              localStorage.removeItem('type');
              localStorage.removeItem('tierId');
              localStorage.removeItem('email');
              localStorage.removeItem('title');
            }
            reject(err);
          });
      });
    },
    storeBusinessProfile({ commit }, { data }) {
      return new Promise((resolve) => {
        commit('select_business_profile', { data });

        localStorage.setItem('selected_business_profile', JSON.stringify(data));
        return resolve('success');
      });
    },
    getBusinessProfile({ state }) {
      return new Promise((resolve) => {
        const selectedBusinessProfile = { ...state.selectedBusinessProfile };
        return resolve(selectedBusinessProfile);
      });
    },
    corporateUrlCheck(context, corporate_slug) {
      return new Promise((resolve, reject) => {
        api.request('get', `/api/auth/valid-url?url=${corporate_slug}`)
          .then(result => {
            resolve(result.data.status);
          }).catch(() => {
            reject();
          });
      });
    },
    sendResetRequest(context, email) {
      return new Promise((resolve, reject) => {
        api.request('post', `/api/auth/forgot-password`, {}, { email })
          .then((result) => {
            resolve(result.data);
          }).catch((err) => {
            reject(err.response);
          });
      });
    },
    sendExtranetResetRequest(context, email) {
      return new Promise((resolve, reject) => {
        api.request('post', `/api/extranet/auth/forgot-password`, {}, { email })
          .then((result) => {
            resolve(result.data);
          }).catch((err) => {
            reject(err.response);
          });
      });
    },
    extranetCheckLogin({ commit, dispatch }, user) {
      commit('auth_request');
      return new Promise((resolve, reject) => {
        api.request('post', '/api/extranet/auth/check-login', {}, user)
          .then(resp => {
            const token = resp.data.data.token;
            const userData = resp.data.data;

            commit('TwoFactor/RESET_2FA_STATE', null, { root: true });

            localStorage.setItem('token', token);
            localStorage.setItem('username', userData.name);
            localStorage.setItem('email', userData.email);
            localStorage.setItem('id', userData.id);
            localStorage.setItem('level', userData.level);

            commit('set_login_provider', 'email');
            commit('auth_success', { token, user: userData });
            commit('self_loaded');

            dispatch('auth_check')
              .then(() => {
                console.log('[Auth] Extranet user loaded after OTP check-login');
                resolve(userData);
              })
              .catch(err => {
                console.error('[Auth] Error loading extranet user after OTP check-login:', err);
                resolve(userData);
              });
          }).catch(err => {
            commit('auth_error');
            reject(err);
          });
      });
    },
    sendPasswordReset(context, { password, password_confirmation, token, email }) {
      return new Promise((resolve, reject) => {
        api.request('post', `/api/auth/reset-password`, {}, {
          password,
          password_confirmation,
          token,
          email
        }).then(result => {
          resolve(result.data);
        }).catch(err => {
          reject(err.response);
        });
      });
    },
    checkTokenPasswordReset(context, { token, email }) {
      return new Promise((resolve, reject) => {
        api.request('post', `/api/auth/check-token-reset-password`, {}, {
          token,
          email
        }).then(result => {
          resolve(result.data);
        }).catch(err => {
          reject(err.response);
        });
      });
    },
    async googleLogin(_, payload) {
      try {
        let url = `/api/auth/google`;

        // Get ref parameter from localStorage if available
        const refParam = localStorage.getItem('sso_ref_param');

        const params = new URLSearchParams();

        if (payload.merchant_id) {
          params.append('merchant_id', payload.merchant_id);
        }

        if (payload.corporate_id && payload.dashboard) {
          params.append('corporate_id', payload.corporate_id);
          params.append('dashboard', payload.dashboard);
        }

        // Add ref parameter if available
        if (refParam) {
          params.append('ref', refParam);
        }

        if (payload.state) {
          params.append('state', encodeURIComponent(payload.state));
        }

        if (payload.source) {
          params.append('source', payload.source);
        }

        if (params.toString()) {
          url += `?${params.toString()}`;
        }

        const response = await api.request('get', url);
        if (response.data && response.data.url) {
          const redirectUrl = new URL(response.data.url);

          redirectUrl.searchParams.append('tier_name', response.data.tier_name);
          redirectUrl.searchParams.append('voucher', response.data.voucher);
          redirectUrl.searchParams.append('point', response.data.point);
          redirectUrl.searchParams.append('media', response.data.media);

          window.location.href = redirectUrl.toString();
        }
      } catch (error) {
        console.error("Error during Google login:", error);
      }
    },
    async appleLogin({ commit, dispatch }) {
      commit('auth_request');
      try {
        const { default: appleAuthService } = await import('@/services/appleAuth');
        const response = await appleAuthService.login();

        const token = response.data?.token || response.token;
        const userData = response.data?.user || response.user;

        if (!token || !userData) {
          throw new Error('Invalid response payload from Apple Login verification');
        }

        commit('TwoFactor/RESET_2FA_STATE', null, { root: true });

        localStorage.setItem('token', token);
        localStorage.setItem('username', userData.name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim());
        localStorage.setItem('email', userData.email || '');
        localStorage.setItem('id', userData.id || '');
        localStorage.setItem('level', userData.member ? 'member' : 'admin');
        localStorage.setItem('corporate_id', userData.corporate_id || '');
        localStorage.setItem('member', JSON.stringify(userData.member || true));
        localStorage.setItem('user', JSON.stringify(userData));

        commit('auth_success', { token, user: userData });

        if (Capacitor.isNativePlatform()) {
          storageService.syncToNative().catch((err) => {
            console.error('[Auth Store] Failed to sync to native storage after appleLogin:', err);
          });
        }

        await dispatch('auth_check');

        if (Capacitor.isNativePlatform()) {
          try {
            await pushNotificationHandler.registerDevice();
          } catch (error) {
            console.error('[Auth Store] Push notification registration failed after appleLogin:', error);
          }
        }

        return response;
      } catch (error) {
        commit('auth_error');
        throw error;
      }
    },
    async handleGoogleAutoLogin({ dispatch }, userData) {
      try {
        const response = await api.request('post', `/api/auth/login`, {}, {
          email: userData.email,
          password: userData.password,
        });

        if (response.data.success) {
          const { email, password, redirect_url } = response.data;
          await dispatch('login', { email, password });

          window.location.href = redirect_url;
        }
      } catch (error) {
        console.error("Error during automatic login:", error);
      }
    },
    async facebookLogin(_, merchant_id = '') {
      try {
        let url = `/api/auth/facebook`;
        if (merchant_id) {
          url += `?merchant_id=${merchant_id}`;
        }

        const response = await api.request('get', url, { withCredentials: true });
        if (response.data && response.data.url) {
          window.location.href = response.data.url;
        }
      } catch (error) {
        console.error("Error during Facebook login:", error);
      }
    },
    async linkedinLogin(_, merchant_id = '') {
      try {
        let url = `/api/auth/linkedin`;
        if (merchant_id) {
          url += `?merchant_id=${merchant_id}`;
        }

        const response = await api.request('get', url, { withCredentials: true });
        if (response.data && response.data.url) {
          window.location.href = response.data.url;
        }
      } catch (error) {
        console.error("Error during Linkedin login:", error);
      }
    },

    nativeLogin({ commit, dispatch }, user) {
      commit('auth_request');
      return new Promise((resolve, reject) => {
        const payload = {
          email: user.email,
          password: user.password,
        };

        api.request('post', '/api/auth/native/login', {}, payload)
          .then(resp => {
            if (resp.data.data && resp.data.data.requires_2fa) {
              commit('TwoFactor/SET_TEMP_TOKEN', resp.data.data.temp_token, { root: true });
              commit('TwoFactor/SET_SHOW_VERIFY_MODAL', true, { root: true });
              commit('auth_2fa_pending');
              return resolve({ requires_2fa: true });
            }

            const token = resp.data.data.token;
            const userData = resp.data.data;

            commit('TwoFactor/RESET_2FA_STATE', null, { root: true });

            // Simpan data ke localStorage
            localStorage.setItem('token', token);
            localStorage.setItem('username', userData.name);
            localStorage.setItem('email', userData.email);
            localStorage.setItem('id', userData.id);
            localStorage.setItem('level', userData.level);
            localStorage.setItem('member', JSON.stringify(userData.member));
            localStorage.setItem('corporate_id', userData.member?.corporate_id ?? '');
            localStorage.setItem('branch_id', '');
            localStorage.setItem('merchant_id', '');
            localStorage.setItem('user', JSON.stringify(userData));

            commit('auth_success', { token, user: userData });

            const businessData = {
              corporate_id: userData.member?.corporate_id ?? null,
              branch_id: null,
              merchant_id: null,
            };
            localStorage.setItem('selected_business_profile', JSON.stringify(businessData));
            commit('select_business_profile', { data: businessData });

            // Sync ke native storage untuk auto-login
            storageService.syncToNative().catch((err) => {
              console.error('[Auth] Failed to sync to native storage after nativeLogin:', err);
            });

            // Load auth_check (auth/self) untuk update user data lengkap
            dispatch('auth_check')
              .then(async () => {
                console.log('[Auth] Native user loaded after nativeLogin');

                // Register push notifications
                if (Capacitor.isNativePlatform()) {
                  try {
                    const { pushNotificationHandler } = await import('@/mobile/plugins/pushNotification');
                    await pushNotificationHandler.registerDevice();
                  } catch (error) {
                    console.error('[Auth] Push notification registration failed after nativeLogin:', error);
                  }
                }

                resolve(userData);
              })
              .catch(err => {
                console.error('[Auth] Error loading user data after nativeLogin:', err);
                resolve(userData);
              });
          })
          .catch(err => {
            commit('auth_error');
            reject(err);
          });
      });
    },
  },
  getters: {
    isLoggedIn: state => !!state.token,
    authStatus: state => state.status,
    getUser: state => state.currUser,
    businessProfiles: state => state.businessProfiles,
    selectedBusinessProfile: state => state.selectedBusinessProfile,
    userInfo: state => state.userInfo
  }
};